// Thin wrapper around the App Bridge v4 global (`window.shopify`) that loads
// from the CDN script tag in index.html. Exposes exactly what the dashboard
// needs: detect embedded context and mint a session token on demand.

interface ShopifyGlobal {
  readonly idToken: () => Promise<string>;
  readonly config: {
    readonly apiKey?: string;
    readonly shop?: string;
    readonly host?: string;
  };
}

declare global {
  interface Window {
    shopify?: ShopifyGlobal;
  }
}

export class AppBridgeUnavailableError extends Error {
  constructor(reason: string) {
    super(`App Bridge unavailable: ${reason}`);
    this.name = "AppBridgeUnavailableError";
  }
}

// Embedded === loaded inside Shopify Admin. We use multiple signals because
// any single one can fail in normal navigation:
//   1. `?host=` in the current URL — present on the initial Shopify load,
//      but React Router strips it after the first internal navigation.
//   2. `window.shopify` global — defined whenever the App Bridge script
//      successfully bootstrapped (it only does so under a Shopify iframe).
//   3. `window.self !== window.top` — we're inside an iframe. CSP
//      frame-ancestors restricts framing to admin.shopify.com / *.myshopify.com,
//      so being framed at all means it's a Shopify embed.
// ANY positive signal flips us to embedded; only when all three say "no"
// do we fall back to the dev sign-in form.
let embeddedOverride: boolean | null = null;

export function isEmbedded(): boolean {
  if (embeddedOverride !== null) return embeddedOverride;
  if (typeof window === "undefined") return false;
  const search = window.location?.search;
  if (typeof search === "string" && new URLSearchParams(search).has("host")) {
    return true;
  }
  if (typeof window.shopify !== "undefined") return true;
  try {
    if (window.self !== window.top) return true;
  } catch {
    // Cross-origin frame access throws — that itself proves we're framed.
    return true;
  }
  return false;
}

export async function getShopifySessionToken(): Promise<string> {
  if (typeof window === "undefined" || !window.shopify) {
    throw new AppBridgeUnavailableError("shopify global missing");
  }
  const token = await window.shopify.idToken();
  if (!token || typeof token !== "string") {
    throw new AppBridgeUnavailableError("empty token");
  }
  return token;
}

// Exposed for tests. In node (vitest default env) there's no `window`, so we
// polyfill a minimal one so the same code under test reads `window.shopify`
// the same way it would in the browser.
export function __setShopifyGlobal(shopify: ShopifyGlobal | undefined): void {
  const g = globalThis as unknown as { window?: { shopify?: ShopifyGlobal } };
  if (typeof g.window === "undefined") g.window = {};
  if (shopify) {
    g.window.shopify = shopify;
  } else {
    delete g.window.shopify;
  }
}

// Tests force-toggle the embedded flag without needing a DOM. Pass `null` to
// restore runtime detection.
export function __setEmbeddedForTests(value: boolean | null): void {
  embeddedOverride = value;
}
